import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@devhop/ui";
import { useEffect, useState } from "react";
import {
  useOrderSpecialWorkQuery,
  useUpdateOrderSpecialWork,
} from "@/modules/cf-feed/presentation/api/queries";

type SpecialWorkDialogProps = {
  feedId: string;
  orderId: string | null;
  onOpenChange: (open: boolean) => void;
};

type UnitValues = Record<number, { name: string; number: string }>;

export function SpecialWorkDialog({
  feedId,
  orderId,
  onOpenChange,
}: SpecialWorkDialogProps) {
  const query = useOrderSpecialWorkQuery(feedId, orderId);
  const update = useUpdateOrderSpecialWork(feedId);
  const [values, setValues] = useState<UnitValues>({});
  const data = query.data;

  useEffect(() => {
    if (!data) return;
    setValues(
      Object.fromEntries(
        data.units.map((unit) => [
          unit.unitIndex,
          { name: unit.name ?? "", number: unit.number ?? "" },
        ]),
      ),
    );
  }, [data]);

  const requiresName = Boolean(
    data?.requirements.some((requirement) => requirement.code === "NAME_PRINT"),
  );
  const requiresNumber = Boolean(
    data?.requirements.some(
      (requirement) => requirement.code === "NUMBER_PRINT",
    ),
  );

  return (
    <Dialog open={Boolean(orderId)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>ຂໍ້ມູນງານພິມພິເສດ</DialogTitle>
        </DialogHeader>

        {query.isLoading ? (
          <div className="rounded-md border border-dashed p-8 text-center text-muted-foreground text-sm">
            ກຳລັງໂຫຼດຂໍ້ມູນ...
          </div>
        ) : query.isError ? (
          <div
            role="alert"
            className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-destructive text-sm"
          >
            ໂຫຼດຂໍ້ມູນງານພິມບໍ່ສຳເລັດ
          </div>
        ) : !data || data.requirements.length === 0 ? (
          <div className="rounded-md border border-dashed p-8 text-center text-muted-foreground text-sm">
            ອໍເດີ້ນີ້ບໍ່ມີງານພິມພິເສດ
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-muted-foreground text-sm">
              ຕື່ມຂໍ້ມູນໃຫ້ຄົບ {data.quantity} ຊິ້ນ ເພື່ອສົ່ງຕໍ່ໄປລະບົບ POS
            </p>
            <div className="max-h-[55vh] space-y-3 overflow-y-auto pr-1">
              {data.units.map((unit) => {
                const unitValues = values[unit.unitIndex] ?? {
                  name: "",
                  number: "",
                };
                return (
                  <div
                    key={unit.unitIndex}
                    className="grid gap-3 rounded-md border bg-muted/30 p-3 sm:grid-cols-2"
                  >
                    <p className="font-medium text-sm sm:col-span-2">
                      ຊິ້ນທີ {unit.unitIndex + 1}
                    </p>
                    {requiresName ? (
                      <div className="space-y-1">
                        <Label htmlFor={`special-name-${unit.unitIndex}`}>
                          ຊື່ທີ່ຈະພິມ
                        </Label>
                        <Input
                          id={`special-name-${unit.unitIndex}`}
                          maxLength={100}
                          value={unitValues.name}
                          disabled={update.isPending}
                          onChange={(event) =>
                            setValues((current) => ({
                              ...current,
                              [unit.unitIndex]: {
                                ...(current[unit.unitIndex] ?? {
                                  name: "",
                                  number: "",
                                }),
                                name: event.target.value,
                              },
                            }))
                          }
                        />
                      </div>
                    ) : null}
                    {requiresNumber ? (
                      <div className="space-y-1">
                        <Label htmlFor={`special-number-${unit.unitIndex}`}>
                          ເບີທີ່ຈະພິມ
                        </Label>
                        <Input
                          id={`special-number-${unit.unitIndex}`}
                          maxLength={20}
                          value={unitValues.number}
                          disabled={update.isPending}
                          onChange={(event) =>
                            setValues((current) => ({
                              ...current,
                              [unit.unitIndex]: {
                                ...(current[unit.unitIndex] ?? {
                                  name: "",
                                  number: "",
                                }),
                                number: event.target.value,
                              },
                            }))
                          }
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            ຍົກເລີກ
          </Button>
          <Button
            type="button"
            disabled={!orderId || !data || update.isPending || query.isError}
            onClick={async () => {
              if (!orderId || !data) return;
              await update.mutateAsync({
                orderId,
                units: data.units.map((unit) => ({
                  unitIndex: unit.unitIndex,
                  name: values[unit.unitIndex]?.name.trim() || null,
                  number: values[unit.unitIndex]?.number.trim() || null,
                })),
              });
              onOpenChange(false);
            }}
          >
            {update.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກຂໍ້ມູນ"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
